import type { ReportingComparisonDatasetDto } from '../response/comparison';
import {
  formatComparisonCurrencyShort,
  formatComparisonDecimalShort,
  getComparisonMetricCaption,
  type ComparisonRanges,
} from './comparison-format';

/**
 * The Comparison report's charts, drawn as SVG.
 *
 * One drawing for every place a chart is printed. The PDF embeds the SVG as it is, and the Excel
 * export turns the same SVG into a picture in the browser — it used to paint its own onto a
 * canvas, which a server has no way to do, so a second renderer would have meant the same three
 * charts drawn twice and drifting apart.
 *
 * Plain SVG only — rects, lines, polylines, circles and text — because the PDF side reads it with
 * svg-to-pdfkit, which does not do CSS, filters or measured text. Text is therefore laid out on
 * estimated widths, and the font list starts with the face the PDF registers.
 */

export const COMPARISON_CHART_FONT_FAMILY = 'Helvetica Neue, Helvetica, Arial, sans-serif';

const TITLE_COLOR = '#1f3b57';
const MUTED_COLOR = '#5b6b79';
const GRID_COLOR = '#d7e0ea';
const AXIS_COLOR = '#cfd9e2';

export const COMPARISON_CHART_COLORS = {
  currentPremium: '#0f6cbd',
  comparisonPremium: '#7f8c9f',
  currentPolicies: '#198754',
  comparisonPolicies: '#adb5bd',
  increase: '#2d6da3',
  decrease: '#c63f3f',
} as const;

/** How many rows the ranking chart draws: beyond this the bars are too thin to label. */
export const COMPARISON_RANKING_CHART_ROWS = 8;

export const COMPARISON_TREND_CHART_SIZE = { width: 820, height: 220 } as const;
export const COMPARISON_RANKING_CHART_WIDTH = 1680;

export interface ComparisonLineChartSeries {
  name: string;
  color: string;
  values: number[];
}

export interface ComparisonLineChartInput {
  title: string;
  labels: string[];
  series: ComparisonLineChartSeries[];
  formatValue: (value: number) => string;
  width?: number;
  height?: number;
}

export interface ComparisonBarChartRow {
  label: string;
  value: number;
}

export interface ComparisonBarChartInput {
  title: string;
  rows: ComparisonBarChartRow[];
  formatValue: (value: number) => string;
  width?: number;
}

export interface ComparisonChartSvgs {
  premiumTrend: string;
  policyTrend: string;
  ranking: string;
}

/** Escapes text for XML: an agency called "A & B" must not end the document it is printed in. */
export function escapeComparisonSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Rounded so a document is not carried on sixteen-digit coordinates. */
function n(value: number): string {
  return `${Math.round(value * 100) / 100}`;
}

/**
 * How wide Helvetica draws each printable ASCII character, from the space (32) to the tilde (126),
 * in thousandths of its size — taken from the face's metrics file.
 *
 * Helvetica is what the PDF prints these charts in, and Arial, which a browser without Helvetica
 * falls back on, shares its widths. One average for every character will not do: capitals run
 * two-thirds of the size against under half for lower case, so an agency named in capitals
 * printed a fifth wider than it was laid out for, and ran off the chart's edge.
 */
const HELVETICA_WIDTHS: readonly number[] = [
  278, 278, 355, 556, 556, 889, 667, 222, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 222, 556, 556, 500, 556,
  556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334,
  260, 334, 584,
];
const ELLIPSIS = '…';
/** Helvetica Neue, which a Mac draws the Excel export's charts in, runs a few percent wider. */
const WIDTH_ALLOWANCE = 1.05;

/** How wide a line of chart text prints — to lay it out by, as svg-to-pdfkit cannot measure it. */
export function estimateComparisonTextWidth(text: string, fontSize: number): number {
  let units = 0;
  for (const character of text) {
    units += character === ELLIPSIS ? 1000 : (HELVETICA_WIDTHS[character.charCodeAt(0) - 32] ?? 556);
  }

  return (units / 1000) * fontSize * WIDTH_ALLOWANCE;
}

function truncate(text: string, maxWidth: number, fontSize: number): string {
  if (estimateComparisonTextWidth(text, fontSize) <= maxWidth) {
    return text;
  }

  let kept = text;
  while (kept.length > 1 && estimateComparisonTextWidth(`${kept.trimEnd()}${ELLIPSIS}`, fontSize) > maxWidth) {
    kept = kept.slice(0, -1);
  }

  return `${kept.trimEnd()}${ELLIPSIS}`;
}

type TextOptions = {
  size: number;
  color: string;
  anchor?: 'start' | 'middle' | 'end';
  bold?: boolean;
};

/**
 * Text placed by its top or its middle rather than its baseline.
 *
 * svg-to-pdfkit does not read dominant-baseline, so the baseline is worked out here from the
 * font size instead of being left to the renderer.
 */
function text(x: number, y: number, value: string, options: TextOptions, verticalAlign: 'top' | 'middle'): string {
  const baseline = verticalAlign === 'top' ? y + options.size * 0.8 : y + options.size * 0.35;

  return (
    `<text x="${n(x)}" y="${n(baseline)}" font-family="${COMPARISON_CHART_FONT_FAMILY}" ` +
    `font-size="${options.size}"${options.bold ? ' font-weight="bold"' : ''} fill="${options.color}"` +
    `${options.anchor && options.anchor !== 'start' ? ` text-anchor="${options.anchor}"` : ''}>` +
    `${escapeComparisonSvgText(value)}</text>`
  );
}

function open(width: number, height: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<rect x="0.5" y="0.5" width="${n(width - 1)}" height="${n(height - 1)}" fill="#ffffff" stroke="${GRID_COLOR}" stroke-width="1"/>`
  );
}

function title(value: string): string {
  return text(18, 16, value, { size: 16, color: TITLE_COLOR, bold: true }, 'top');
}

/** Legend entries laid right to left from the chart's top right corner, as the export always drew them. */
function legend(entries: Array<{ label: string; color: string }>, right: number, y: number): string {
  let cursor = right;

  return [...entries]
    .reverse()
    .map(entry => {
      cursor -= estimateComparisonTextWidth(entry.label, 12) + 26;
      const markup =
        `<rect x="${n(cursor)}" y="${n(y - 4)}" width="12" height="12" fill="${entry.color}"/>` +
        text(cursor + 18, y + 2, entry.label, { size: 12, color: MUTED_COLOR }, 'middle');
      cursor -= 12;

      return markup;
    })
    .join('');
}

/**
 * Which axis labels to print: every one if they fit, otherwise every nth.
 *
 * A year of weekly buckets is some forty labels, which the canvas version printed on top of each
 * other; thinning keeps the axis readable at any range.
 */
function pickLabelStep(labels: string[], plotWidth: number): number {
  if (labels.length <= 1) {
    return 1;
  }

  const widest = Math.max(...labels.map(label => estimateComparisonTextWidth(label, 12)));
  const perLabel = plotWidth / (labels.length - 1);

  return Math.max(1, Math.ceil((widest + 12) / Math.max(perLabel, 1)));
}

export function buildComparisonLineChartSvg(input: ComparisonLineChartInput): string {
  const width = input.width ?? COMPARISON_TREND_CHART_SIZE.width;
  const height = input.height ?? COMPARISON_TREND_CHART_SIZE.height;
  const left = 64;
  const right = width - 28;
  const top = 52;
  const bottom = height - 44;
  const values = input.series.flatMap(series => series.values);
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const range = Math.max(maxValue - minValue, 1);
  const plotWidth = right - left;
  const plotHeight = bottom - top;
  const stepX = input.labels.length > 1 ? plotWidth / (input.labels.length - 1) : 0;
  const toY = (value: number): number => bottom - ((value - minValue) / range) * plotHeight;
  const toX = (index: number): number => left + stepX * index;

  const parts: string[] = [open(width, height), title(input.title)];

  for (let index = 0; index < 4; index += 1) {
    const y = top + (plotHeight / 3) * index;
    parts.push(
      `<line x1="${n(left)}" y1="${n(y)}" x2="${n(right)}" y2="${n(y)}" stroke="${GRID_COLOR}" stroke-width="1"/>`,
      text(
        left - 8,
        y,
        input.formatValue(maxValue - (range / 3) * index),
        { size: 12, color: MUTED_COLOR, anchor: 'end' },
        'middle',
      ),
    );
  }

  input.series.forEach(series => {
    if (!series.values.length) {
      return;
    }

    const points = series.values.map((value, index) => `${n(toX(index))},${n(toY(value))}`).join(' ');
    parts.push(`<polyline points="${points}" fill="none" stroke="${series.color}" stroke-width="3"/>`);
    series.values.forEach((value, index) => {
      parts.push(
        `<circle cx="${n(toX(index))}" cy="${n(toY(value))}" r="4" fill="#ffffff" stroke="${series.color}" stroke-width="2"/>`,
      );
    });
  });

  const labelStep = pickLabelStep(input.labels, plotWidth);
  input.labels.forEach((label, index) => {
    if (index % labelStep !== 0) {
      return;
    }

    parts.push(text(toX(index), bottom + 10, label, { size: 12, color: MUTED_COLOR, anchor: 'middle' }, 'top'));
  });

  parts.push(
    legend(
      input.series.map(series => ({ label: series.name, color: series.color })),
      width - 24,
      22,
    ),
  );
  parts.push('</svg>');

  return parts.join('');
}

/**
 * Bars from a zero line on one scale, gains to the right in blue and losses to the left in red.
 *
 * The zero line stands where the figures put it: at the left of the plot when nothing was lost —
 * as in a ranking of the biggest gains, which is what this chart is usually handed — at the right
 * when nothing was gained, and in between, in proportion, when both. Fixed in the middle, it left
 * half of nearly every chart empty and the bars squeezed into the other half.
 *
 * The names get the room the longest of them needs, up to a third of the chart; a longer one is
 * cut short with an ellipsis rather than run off the edge. Each figure is kept clear of the
 * names and of the frame.
 *
 * Its height follows its rows, so a short ranking does not sit in a box sized for eight.
 */
export function buildComparisonBarChartSvg(input: ComparisonBarChartInput): string {
  const width = input.width ?? COMPARISON_RANKING_CHART_WIDTH;
  const inset = 18;
  const top = 54;
  const rowHeight = 28;
  const textSize = 12;
  const figureGap = 8;
  const height = Math.max(top + input.rows.length * rowHeight + 20, 120);
  const measure = (value: string): number => estimateComparisonTextWidth(value, textSize);

  const labels = input.rows.map(row => truncate(row.label, Math.min(300, width / 3), textSize));
  const labelRight = inset + Math.max(0, ...labels.map(measure));
  const plotLeft = labelRight + 14;
  const plotRight = width - inset;

  const figures = input.rows.map(row => input.formatValue(row.value));
  const figureRooms = figures.map(figure => measure(figure) + figureGap);
  const gainFigureRoom = Math.max(0, ...figureRooms.filter((_, index) => input.rows[index].value >= 0));
  const lossFigureRoom = Math.max(0, ...figureRooms.filter((_, index) => input.rows[index].value < 0));
  const largestGain = Math.max(0, ...input.rows.map(row => row.value));
  const largestLoss = Math.max(0, ...input.rows.map(row => -row.value));
  const scale = Math.max(plotRight - plotLeft - gainFigureRoom - lossFigureRoom, 0) / (largestGain + largestLoss || 1);
  const zeroX = plotLeft + lossFigureRoom + largestLoss * scale;

  const parts: string[] = [
    open(width, height),
    title(input.title),
    `<line x1="${n(zeroX)}" y1="${n(top - 8)}" x2="${n(zeroX)}" y2="${n(top + input.rows.length * rowHeight + 4)}" stroke="${AXIS_COLOR}" stroke-width="1"/>`,
  ];

  input.rows.forEach((row, index) => {
    const y = top + index * rowHeight;
    const barWidth = Math.abs(row.value) * scale;
    const isGain = row.value >= 0;
    const barStart = isGain ? zeroX : zeroX - barWidth;
    const color = isGain ? COMPARISON_CHART_COLORS.increase : COMPARISON_CHART_COLORS.decrease;

    parts.push(
      `<rect x="${n(barStart)}" y="${n(y)}" width="${n(barWidth)}" height="18" fill="${color}"/>`,
      text(labelRight, y + 9, labels[index], { size: textSize, color: TITLE_COLOR, anchor: 'end' }, 'middle'),
      text(
        isGain ? barStart + barWidth + figureGap : barStart - figureGap,
        y + 9,
        figures[index],
        { size: textSize, color: TITLE_COLOR, anchor: isGain ? 'start' : 'end' },
        'middle',
      ),
    );
  });

  parts.push('</svg>');

  return parts.join('');
}

/**
 * A dataset's three charts, named and coloured as the page draws them — each series carries its
 * column's caption, "2026 Premium" rather than "Current Premium".
 */
export function buildComparisonDatasetChartSvgs(
  dataset: Pick<ReportingComparisonDatasetDto, 'trend' | 'ranking'>,
  ranges: ComparisonRanges,
  options: { trendWidth?: number; trendHeight?: number; rankingWidth?: number } = {},
): ComparisonChartSvgs {
  const labels = dataset.trend.map(point => point.label);

  return {
    premiumTrend: buildComparisonLineChartSvg({
      title: 'Premium trend',
      labels,
      width: options.trendWidth,
      height: options.trendHeight,
      formatValue: formatComparisonCurrencyShort,
      series: [
        {
          name: getComparisonMetricCaption('current', 'Premium', ranges),
          color: COMPARISON_CHART_COLORS.currentPremium,
          values: dataset.trend.map(point => point.currentPremium),
        },
        {
          name: getComparisonMetricCaption('comparison', 'Premium', ranges),
          color: COMPARISON_CHART_COLORS.comparisonPremium,
          values: dataset.trend.map(point => point.comparisonPremium),
        },
      ],
    }),
    policyTrend: buildComparisonLineChartSvg({
      title: 'Policy trend',
      labels,
      width: options.trendWidth,
      height: options.trendHeight,
      formatValue: formatComparisonDecimalShort,
      series: [
        {
          name: getComparisonMetricCaption('current', 'Policies', ranges),
          color: COMPARISON_CHART_COLORS.currentPolicies,
          values: dataset.trend.map(point => point.currentPolicies),
        },
        {
          name: getComparisonMetricCaption('comparison', 'Policies', ranges),
          color: COMPARISON_CHART_COLORS.comparisonPolicies,
          values: dataset.trend.map(point => point.comparisonPolicies),
        },
      ],
    }),
    ranking: buildComparisonBarChartSvg({
      title: 'Premium difference ranking',
      width: options.rankingWidth,
      formatValue: formatComparisonCurrencyShort,
      rows: dataset.ranking.slice(0, COMPARISON_RANKING_CHART_ROWS).map(row => ({
        label: row.entityName,
        value: row.premiumDifference,
      })),
    }),
  };
}
